// ═══════════════════════════════════════════════════════════════════════════
//  SubscriptionTab.jsx  ·  STUDENT FINANCIAL HUB — Silicon Valley Edition
//  Location: src/components/dashboard/subscription/SubscriptionTab.jsx
//
//  RENDERS SEGÚN TIPO DE USUARIO:
//   🟡 PREMIUM   → Plan + Billing + Tokens + Acciones + Calendario
//   🔵 B2B       → Empresa patrocinadora + Modalidad + Tokens + Fechas
//   ⚪ FREEMIUM  → Countdown Trial + Tokens + CTA Upgrade + Funnel
//
//  SECCIONES:
//   [1] Plan Hero         → Badge plan activo, estado, fecha próximo pago
//   [2] Token Gauge       → Medidor visual tipo "tanque de combustible"
//   [3] Teacher Card      → Teacher asignado, clases, próxima sesión
//   [4] Calendar Strip    → Próximas 3 fechas (clases + pagos)
//   [5] Alerts Feed       → Notificaciones del sistema
//   [6] Actions Panel     → Cancelar / Upgrade / Contactar
// ═══════════════════════════════════════════════════════════════════════════

import { createPortal } from "react-dom";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../../../pages/PricingPage.css"; // Importar CSS del modal
import "./SubscriptionTab.css";

import Valoracion from '../../shared/Valoracion.jsx';
import { enlaceWhatsApp } from '../../../contacto';
// ─────────────────────────────────────────────────────────────────────────────
//  API HOOKS
// ─────────────────────────────────────────────────────────────────────────────

function useSubscriptionData() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch("/api/dashboard/subscription", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setData(json);
        } catch (e) {
            setError(e.message);
            // No fallback a datos demo en producción
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    return { data, loading, error, refetch: load };
}

// ─────────────────────────────────────────────────────────────────────────────
//  SU CUPÓN, SI TIENE UNO — 20-09-2026
//
//  Rob: «mira, en la sección de suscripción también, ahí también tiene que
//  venir: hey, tienes un descuento especial, que sea antes de determinado
//  tiempo».
//
//  Aquí NO se aplica el cupón (eso pasa en los planes, que es donde decide):
//  esto es el recordatorio. Por eso se usa `/mio` y no `/aplicar` — entrar a
//  ver tu suscripción no es estar mirando precios, y avisar a gerencia de que
//  «está a punto» cada vez que abre esta pestaña sería ruido.
// ─────────────────────────────────────────────────────────────────────────────

function useCupon() {
    const [cupon, setCupon] = useState(null);

    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await fetch("/api/promociones/mio", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const json = await res.json();
                if (vivo) setCupon(json?.cupon || null);
            } catch { /* sin cupón, sin ruido */ }
        })();
        return () => { vivo = false; };
    }, []);

    return cupon;
}

function CuponCard({ cupon, onIr }) {
    if (!cupon) return null;
    const ultimo = cupon.dias_restantes <= 1;
    return (
        <div className="sub-cupon-card" data-prueba="cupon-suscripcion">
            <div className="sub-cupon-top">
                <span className="sub-cupon-icon">🎁</span>
                <span className={`sub-cupon-dias ${ultimo ? "--urge" : ""}`}>
                    {cupon.dias_restantes === 0
                        ? "Vence hoy"
                        : cupon.dias_restantes === 1
                            ? "Te queda 1 día"
                            : `Te quedan ${cupon.dias_restantes} días`}
                </span>
            </div>
            <h3 className="sub-cupon-title">
                Tienes un {Number(cupon.pct).toFixed(0)}% de descuento
            </h3>
            <p className="sub-cupon-sub">
                En tu primer pago, con el cupón <strong>{cupon.codigo}</strong>.
                Válido hasta el {cupon.vence_bonito}.
            </p>
            <button className="sub-cupon-btn" onClick={onIr} data-prueba="cupon-ir">
                Ver mis planes con el descuento →
            </button>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  CONSTANTS & HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const PLAN_COLORS = {
    Free: "#9ca3af",
    Jumpstart: "#c084fc",
    Speed: "#FFE234",
    Boost: "#00FFFF",
    Premium: "#f472b6",
    B2B: "#60a5fa",
    "Free Trial": "#c084fc",
};


// ─── Checkout Modal (Importado localmente para SubscriptionTab) ────────────
function CheckoutModal({ plan, onClose }) {
    const [stage, setStage] = useState('options');
    const [closing, setClosing] = useState(false);

    const handleClose = () => {
        setClosing(true);
        setTimeout(onClose, 300);
    };

    const handleWhatsApp = (planName) => {
        // Por el helper compartido, como la tienda: el número vive en un solo
        // sitio y el texto pasa por el limpiador de WhatsApp (22-09-2026).
        window.open(enlaceWhatsApp(`¡Hola! Quiero activar mi plan "${planName}" con transferencia SPEI. ¿Me puedes guiar?`), '_blank', 'noopener');
    };

    return createPortal(
        <div className={`prc-overlay ${closing ? 'prc-overlay-out' : ''}`} onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className={`prc-modal ${closing ? 'prc-modal-out' : ''}`} style={{ '--plan-color': plan?.color || '#ffff00', zIndex: 99999 }}>
                <button className="prc-modal-close" onClick={handleClose}>✕</button>

                {stage === 'options' ? (
                    <>
                        <div className="prc-modal-header">
                            <div className="prc-modal-icon">🚀</div>
                            <h3 className="prc-modal-title">¡Un paso para cambiar tu inglés!</h3>
                            <p className="prc-modal-sub">Adquiriendo: <strong className="prc-modal-plan-name" style={{ color: plan?.color || '#ffff00' }}>{plan?.name}</strong></p>
                        </div>

                        <div className="prc-payment-grid">
                            <button className="prc-pay-btn prc-pay-spei" onClick={() => handleWhatsApp(plan?.name)}>
                                <span className="prc-pay-icon">🏦</span>
                                <div>
                                    <div className="prc-pay-label">Transferencia SPEI</div>
                                    <div className="prc-pay-sub prc-pay-spei-sub">⚡ Activación en menos de 1 hora</div>
                                </div>
                                <span className="prc-pay-arrow">›</span>
                            </button>

                            <div className="prc-pay-divider"><span>— o elige otro método —</span></div>

                            <button className="prc-pay-btn prc-pay-stripe" onClick={() => setStage('maintenance')}>
                                <span className="prc-pay-icon">💳</span>
                                <div>
                                    <div className="prc-pay-label">Stripe</div>
                                    <div className="prc-pay-sub">Tarjeta de crédito / débito</div>
                                </div>
                                <span className="prc-pay-arrow">›</span>
                            </button>

                            <button className="prc-pay-btn prc-pay-mp" onClick={() => setStage('maintenance')}>
                                <span className="prc-pay-icon">🔵</span>
                                <div>
                                    <div className="prc-pay-label">MercadoPago</div>
                                    <div className="prc-pay-sub">Rápido y seguro</div>
                                </div>
                                <span className="prc-pay-arrow">›</span>
                            </button>
                        </div>

                        <div className="prc-modal-trust">
                            <span>🔒 Pago 100% seguro</span>
                            <span>·</span>
                            <span>🛡️ Sin cargos ocultos</span>
                            <span>·</span>
                            <span>✉️ Confirmación inmediata</span>
                        </div>
                    </>
                ) : (
                    <div className="prc-maintenance">
                        <div className="prc-maintenance-icon">⚡</div>
                        <h4>¡Perfecto! Estás a un paso</h4>
                        <p>
                            Nuestros métodos de pago automáticos están temporalmente en mantenimiento
                            mientras actualizamos la plataforma. Para garantizarte el acceso{' '}
                            <strong>hoy mismo</strong>, te atendemos personalmente por WhatsApp
                            en menos de 10 minutos.
                        </p>
                        <button className="prc-whatsapp-btn" onClick={() => handleWhatsApp(plan?.name)}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            Continuar por WhatsApp
                        </button>
                        <button className="prc-back-btn" onClick={() => setStage('options')}>← Volver a opciones de pago</button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

const STATUS_CFG = {
    active: { label: "Activo", color: "#00ff88", bg: "rgba(0,255,136,.12)", border: "rgba(0,255,136,.3)" },
    overdue: { label: "Pago Vencido", color: "#ff5e7e", bg: "rgba(255,94,126,.12)", border: "rgba(255,94,126,.3)" },
    paused: { label: "Suspendido", color: "#9ca3af", bg: "rgba(156,163,175,.1)", border: "rgba(156,163,175,.2)" },
    cancelled: { label: "Cancelado", color: "#ff5e7e", bg: "rgba(255,94,126,.08)", border: "rgba(255,94,126,.2)" },
};

const GATEWAY_MAP = {
    stripe: { icon: "💳", label: "Stripe" },
    mercadopago: { icon: "🔵", label: "MercadoPago" },
    manual: { icon: "💵", label: "Manual" },
    "—": { icon: "—", label: "—" },
};

const MODALITY_MAP = {
    online: { icon: "🖥️", label: "Online" },
    presencial: { icon: "🏫", label: "Presencial" },
    grupal: { icon: "👥", label: "Grupal" },
};

const FORMAT_MAP = {
    cerf: { icon: "🌍", label: "CERF (A1-C2)" },
    business: { icon: "💼", label: "Business English" },
};

function fmtDate(iso) {
    if (!iso || iso === "—") return "—";
    const d = new Date(iso.slice(0, 10) + "T12:00:00");
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtShortDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso.slice(0, 10) + "T12:00:00");
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}

function fmtTime(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

function diffDays(iso) {
    if (!iso) return null;
    return Math.ceil((new Date(iso.slice(0, 10) + "T12:00:00") - Date.now()) / 86_400_000);
}

// ─────────────────────────────────────────────────────────────────────────────
//  SKELETON
// ─────────────────────────────────────────────────────────────────────────────

function Skeleton({ w = "100%", h = 16, r = 8 }) {
    return (
        <span className="sub-skeleton" style={{ width: w, height: h, borderRadius: r }} />
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TOKEN GAUGE — el elemento más memorable
// ─────────────────────────────────────────────────────────────────────────────

// ── EL MEDIDOR MIRA EL SALDO, NO LA RESTA (20-09-2026) ──────────────────────
// Rob compró dos recargas de 1.000 y esta rueda decía «100% usado · ❌ Agotado
// · 0 restantes». Calculaba lo que quedaba restando lo gastado al límite del
// plan, y una recarga no cabe en esa resta: por definición pone el saldo POR
// ENCIMA del tope del mes.
//
// Ahora lo que queda lo dice el servidor (`tokens_remaining`), que es la misma
// cifra que ve en su menú y en su chat. Y cuando hay recarga —saldo mayor que
// el plan— la rueda se llena y lo dice, en vez de marcar rojo.
function TokenGauge({ used, max, left: leftReal, color, loading }) {
    const left = Number.isFinite(leftReal) ? leftReal : max - used;
    const conRecarga = left > max;
    // El porcentaje que se enseña es el de energía USADA de su mes. Con
    // recarga encima no ha gastado nada de lo suyo: 0 %.
    const pct = conRecarga ? 0
        : max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
    const isLow = !conRecarga && left > 0 && (left / (max || 1)) <= 0.2;
    const isEmpty = !conRecarga && left <= 0;

    const gaugeColor = isEmpty
        ? "#ff5e7e"
        : isLow
            ? "#f5a623"
            : color || "#00FFFF";

    // SVG arc parameters
    const cx = 80, cy = 80, r = 64;
    const startAngle = -220;
    const sweepAngle = 260;

    function polarToXY(angle, radius) {
        const rad = ((angle - 90) * Math.PI) / 180;
        return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
    }

    function describeArc(start, sweep) {
        const end = start + sweep;
        const s = polarToXY(start, r);
        const e = polarToXY(end, r);
        const large = Math.abs(sweep) > 180 ? 1 : 0;
        const dir = sweep > 0 ? 1 : 0;
        return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} ${dir} ${e.x} ${e.y}`;
    }

    const filledSweep = (pct / 100) * sweepAngle;

    return (
        <div className="sub-gauge-wrap">
            <div className="sub-gauge-container">
                <svg viewBox="0 0 160 160" className="sub-gauge-svg">
                    {/* Track */}
                    <path
                        d={describeArc(startAngle, sweepAngle)}
                        fill="none"
                        stroke="var(--sub-border)"
                        strokeWidth={10}
                        strokeLinecap="round"
                    />
                    {/* Fill */}
                    {!loading && (
                        <path
                            d={describeArc(startAngle, filledSweep)}
                            fill="none"
                            stroke={gaugeColor}
                            strokeWidth={10}
                            strokeLinecap="round"
                            className="sub-gauge-fill"
                            style={{
                                filter: `drop-shadow(0 0 6px ${gaugeColor})`,
                                transition: "stroke-dashoffset .8s cubic-bezier(.4,0,.2,1)",
                            }}
                        />
                    )}
                    {/* Glow dot at head */}
                    {!loading && filledSweep > 0 && (
                        (() => {
                            const pt = polarToXY(startAngle + filledSweep, r);
                            return (
                                <circle
                                    cx={pt.x} cy={pt.y} r={6}
                                    fill={gaugeColor}
                                    style={{ filter: `drop-shadow(0 0 8px ${gaugeColor})` }}
                                />
                            );
                        })()
                    )}
                </svg>

                {/* Center content */}
                <div className="sub-gauge-center">
                    {loading ? (
                        <Skeleton w={60} h={28} r={6} />
                    ) : (
                        <>
                            <div className="sub-gauge-pct sub-adaptive-text" style={{ '--sub-c': gaugeColor }}>
                                {pct}<span style={{ fontSize: 14, fontWeight: 500 }}>%</span>
                            </div>
                            <div className="sub-gauge-label">usado</div>
                            {conRecarga && (
                                <div className="sub-gauge-warn" style={{ color: "#00ff88" }}
                                    data-prueba="con-recarga">
                                    ⚡ Con recarga
                                </div>
                            )}
                            {isLow && !isEmpty && (
                                <div className="sub-gauge-warn">⚠️ Bajo</div>
                            )}
                            {isEmpty && (
                                <div className="sub-gauge-warn" style={{ color: "#ff5e7e" }}>❌ Agotado</div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Numbers below */}
            <div className="sub-gauge-numbers">
                <div className="sub-gauge-stat">
                    <span className="sub-gauge-stat-val sub-adaptive-text" style={{ '--sub-c': gaugeColor }}>
                        {loading ? <Skeleton w={40} h={16} /> : used.toLocaleString()}
                    </span>
                    <span className="sub-gauge-stat-lbl">Usados</span>
                </div>
                <div className="sub-gauge-divider" />
                <div className="sub-gauge-stat">
                    <span className="sub-gauge-stat-val" style={{ color: "var(--sub-tx)" }}>
                        {loading ? <Skeleton w={40} h={16} /> : left.toLocaleString()}
                    </span>
                    <span className="sub-gauge-stat-lbl">Restantes</span>
                </div>
                <div className="sub-gauge-divider" />
                <div className="sub-gauge-stat">
                    <span className="sub-gauge-stat-val" style={{ color: "var(--sub-tx2)" }}>
                        {loading ? <Skeleton w={40} h={16} /> : max.toLocaleString()}
                    </span>
                    <span className="sub-gauge-stat-lbl">
                        {left > max ? "De tu plan" : "Asignados"}
                    </span>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TRIAL COUNTDOWN — solo freemium
// ─────────────────────────────────────────────────────────────────────────────

function TrialCountdown({ trial }) {
    const { days_remaining, total_days, status } = trial;
    const pct = Math.max(0, Math.min(100, (days_remaining / total_days) * 100));
    const isExpired = days_remaining <= 0;
    const isUrgent = days_remaining <= 2 && !isExpired;

    return (
        <div className={`sub-trial-hero${isUrgent || isExpired ? " sub-trial-urgent" : ""}`}>
            {/* Animated ring */}
            <div className="sub-trial-ring-wrap">
                <svg viewBox="0 0 120 120" className="sub-trial-ring">
                    <circle cx={60} cy={60} r={50} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth={8} />
                    <circle
                        cx={60} cy={60} r={50} fill="none"
                        stroke={isUrgent || isExpired ? "#ff5e7e" : "#c084fc"}
                        strokeWidth={8}
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 50}`}
                        strokeDashoffset={`${2 * Math.PI * 50 * (1 - pct / 100)}`}
                        transform="rotate(-90 60 60)"
                        style={{
                            transition: "stroke-dashoffset .8s ease",
                            filter: `drop-shadow(0 0 8px ${isUrgent || isExpired ? "#ff5e7e" : "#c084fc"})`,
                        }}
                    />
                </svg>
                <div className="sub-trial-ring-center">
                    <span
                        className={`sub-trial-days${isUrgent || isExpired ? " sub-trial-days-urgent" : ""}`}
                        style={{ color: isUrgent || isExpired ? "#ff5e7e" : "#c084fc" }}
                    >
                        {days_remaining}
                    </span>
                    <span className="sub-trial-days-lbl">días</span>
                </div>
            </div>

            <div className="sub-trial-info">
                <div className="sub-trial-title">
                    {isExpired ? "🔒 ¡Tu plan ha finalizado!" : isUrgent ? "⏰ ¡Tu trial termina pronto!" : "🌱 Trial Activo"}
                </div>
                <div className="sub-trial-sub">
                    {isExpired
                        ? "Esperamos que hayas disfrutado la experiencia NSL. ¡Elige un plan Premium para seguir aprendiendo sin límites!"
                        : `Te quedan ${days_remaining} día${days_remaining > 1 ? "s" : ""} de prueba gratuita.`}
                </div>
                <div className="sub-trial-progress-row">
                    <div className="sub-trial-bar">
                        <div
                            className="sub-trial-bar-fill"
                            style={{
                                width: `${pct}%`,
                                background: isUrgent
                                    ? "linear-gradient(90deg, #ff5e7e, #ff8c8c)"
                                    : "linear-gradient(90deg, #c084fc, #e879f9)",
                                boxShadow: `0 0 8px ${isUrgent ? "#ff5e7e66" : "#c084fc66"}`,
                            }}
                        />
                    </div>
                    <span style={{ fontSize: 11, color: "var(--sub-tx2)", flexShrink: 0 }}>
                        Día {total_days - days_remaining} / {total_days}
                    </span>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PLAN HERO CARD
// ─────────────────────────────────────────────────────────────────────────────

function PlanHeroCard({ plan, userType, loading }) {
    const navigate = useNavigate();
    const [showCheckout, setShowCheckout] = useState(false);
    const planColor = PLAN_COLORS[plan?.name] || "#00FFFF";
    const statusCfg = STATUS_CFG[plan?.status] || STATUS_CFG.active;
    const gw = GATEWAY_MAP[plan?.gateway] || GATEWAY_MAP["—"];
    const daysLeft = diffDays(plan?.next_billing_date);

    if (loading) return (
        <div className="sub-plan-card sub-card">
            <Skeleton w="40%" h={24} r={6} />
            <div style={{ marginTop: 12 }}><Skeleton w="60%" h={40} /></div>
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <Skeleton w="33%" h={56} r={10} />
                <Skeleton w="33%" h={56} r={10} />
                <Skeleton w="33%" h={56} r={10} />
            </div>
        </div>
    );

    return (
        <div
            className="sub-plan-card sub-card"
            style={{ borderTop: `2px solid ${planColor}` }}
        >
            {/* Glow orb */}
            <div className="sub-glow-orb" style={{ background: planColor }} />

            {/* Header */}
            <div className="sub-plan-header">
                <div className="sub-plan-left">
                    <span className="sub-plan-emoji">{plan.emoji}</span>
                    <div>
                        <div className="sub-plan-name sub-adaptive-text" style={{ '--sub-c': planColor }}>{plan.name}</div>
                        <div className="sub-plan-sub">{userType === "b2b" ? "Plan Corporativo" : "Plan Personal"}</div>
                    </div>
                </div>
                <span
                    className="sub-status-badge sub-adaptive-text"
                    style={{
                        background: statusCfg.bg,
                        '--sub-c': statusCfg.color,
                        borderColor: statusCfg.border,
                    }}
                >
                    <span className="sub-status-dot" style={{ background: statusCfg.color }} />
                    {statusCfg.label}
                </span>
            </div>

            {/* Price (hidden for B2B) */}
            {userType !== "b2b" && (
                <div className="sub-plan-price-row">
                    <span className="sub-plan-price sub-adaptive-text" style={{ '--sub-c': planColor }}>
                        ${plan.price?.toLocaleString("es-MX")}
                    </span>
                    <span className="sub-plan-currency">{plan.currency}/mes</span>
                </div>
            )}

            {/* Stats chips */}
            <div className="sub-plan-stats">
                {userType !== "b2b" && (
                    <div className="sub-stat-chip">
                        <div className="sub-stat-chip-val sub-adaptive-text" style={{ '--sub-c': planColor }}>
                            {plan.next_billing_date ? fmtDate(plan.next_billing_date) : "—"}
                        </div>
                        <div className="sub-stat-chip-lbl">Próximo pago</div>
                    </div>
                )}
                {plan.classes_per_month > 0 && (
                    <div className="sub-stat-chip">
                        <div className="sub-stat-chip-val sub-adaptive-text" style={{ '--sub-c': "#00ff88" }}>
                            {plan.classes_per_month}
                        </div>
                        <div className="sub-stat-chip-lbl">Clases/mes</div>
                    </div>
                )}
                <div className="sub-stat-chip">
                    <div className="sub-stat-chip-val sub-adaptive-text" style={{ '--sub-c': "#f5a623" }}>
                        {plan.token_limit?.toLocaleString()}
                    </div>
                    <div className="sub-stat-chip-lbl">Tokens IA</div>
                </div>
                {userType !== "b2b" && daysLeft !== null && (
                    <div className="sub-stat-chip">
                        <div
                            className="sub-stat-chip-val sub-adaptive-text"
                            style={{ '--sub-c': daysLeft <= 5 ? "#ff5e7e" : daysLeft <= 10 ? "#f5a623" : "var(--sub-tx)" }}
                        >
                            {daysLeft < 0 ? `${Math.abs(daysLeft)}d venc.` : daysLeft === 0 ? "Hoy" : `${daysLeft}d`}
                        </div>
                        <div className="sub-stat-chip-lbl">Para vencer</div>
                    </div>
                )}
            </div>

            {/* Features */}
            {plan.features?.length > 0 && (
                <div className="sub-features-list">
                    {plan.features.slice(0, 4).map((f, i) => (
                        <div key={i} className="sub-feature-item">
                            <span style={{ color: planColor, fontSize: 10 }}>✓</span>
                            <span>{f}</span>
                        </div>
                    ))}
                    {plan.features.length > 4 && (
                        <div className="sub-feature-item" style={{ color: "var(--sub-tx2)", fontStyle: "italic" }}>
                            +{plan.features.length - 4} beneficios más…
                        </div>
                    )}
                </div>
            )}

            {/* Gateway chip */}
            {userType !== "b2b" && plan.gateway && (
                <div className="sub-gateway-chip">
                    {gw.icon} {gw.label}
                    <span className="sub-gateway-dot" />
                    {plan.anchor_day ? `Día ${plan.anchor_day} de cada mes` : "Facturación mensual"}
                </div>
            )}

            {/* Acciones de Vencimiento */}
            {plan?.status === "overdue" && (
                <div className="sub-overdue-actions">
                    <button className="sub-overdue-btn primary" onClick={() => setShowCheckout(true)}>
                        ⚡ Renovar Plan
                    </button>
                    <button className="sub-overdue-btn secondary" onClick={() => navigate("/plans")}>
                        Ver Planes
                    </button>
                </div>
            )}

            {/* Modal de Checkout */}
            {showCheckout && (
                <CheckoutModal 
                    plan={{ ...plan, color: planColor }} 
                    onClose={() => setShowCheckout(false)} 
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  B2B SPONSOR CARD
// ─────────────────────────────────────────────────────────────────────────────

function B2BSponsorCard({ b2b }) {
    const mod = MODALITY_MAP[b2b.modality] || MODALITY_MAP.online;
    const fmt = FORMAT_MAP[b2b.format] || FORMAT_MAP.cerf;
    const endDiff = diffDays(b2b.contract_end);

    return (
        <div
            className="sub-b2b-card sub-card"
            style={{ borderTop: `2px solid ${b2b.company_color || "#60a5fa"}` }}
        >
            <div className="sub-glow-orb" style={{ background: b2b.company_color }} />
            <div className="sub-b2b-header">
                <div className="sub-b2b-logo sub-adaptive-text" style={{ '--sub-c': b2b.company_color, background: `${b2b.company_color}18`, border: `1px solid ${b2b.company_color}33` }}>
                    {b2b.company_emoji}
                </div>
                <div>
                    <div className="sub-b2b-name sub-adaptive-text" style={{ '--sub-c': b2b.company_color }}>{b2b.company_name}</div>
                    <div className="sub-b2b-sub">Patrocinador Corporativo</div>
                </div>
                <span className="sub-status-badge sub-adaptive-text" style={{ background: "rgba(96,165,250,.12)", '--sub-c': "#60a5fa", borderColor: "rgba(96,165,250,.3)" }}>
                    🏢 B2B
                </span>
            </div>

            <div className="sub-b2b-message">{b2b.sponsor_message}</div>

            <div className="sub-b2b-grid">
                {[
                    { icon: mod.icon, label: "Modalidad", val: mod.label },
                    { icon: fmt.icon, label: "Formato", val: fmt.label },
                    {
                        icon: "📅", label: "Contrato hasta",
                        val: b2b.contract_end ? fmtDate(b2b.contract_end) : "Indefinido"
                    },
                    {
                        icon: "⏳", label: "Días restantes",
                        val: endDiff !== null ? (endDiff < 0 ? "Vencido" : `${endDiff}d`) : "—"
                    },
                ].map((row) => (
                    <div key={row.label} className="sub-b2b-grid-item">
                        <span style={{ fontSize: 16 }}>{row.icon}</span>
                        <div>
                            <div className="sub-b2b-grid-val">{row.val}</div>
                            <div className="sub-b2b-grid-lbl">{row.label}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TEACHER CARD
// ─────────────────────────────────────────────────────────────────────────────

function TeacherCard({ teacher, loading }) {
    if (loading) return (
        <div className="sub-teacher-card sub-card">
            <Skeleton w="100%" h={80} r={12} />
        </div>
    );
    if (!teacher) return null;

    const given = teacher.classes_given || 0;
    const total = teacher.classes_contracted || 1;
    const pct = Math.round((given / total) * 100);

    return (
        <div className="sub-teacher-card sub-card">
            <div className="sub-teacher-header">
                <div className="sub-teacher-avatar sub-adaptive-text" style={{ background: `${teacher.avatar_color}22`, '--sub-c': teacher.avatar_color, boxShadow: `0 0 0 2px ${teacher.avatar_color}44` }}>
                    {teacher.ini}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="sub-teacher-name">{teacher.name}</div>
                    <div className="sub-teacher-level">{teacher.level}</div>
                </div>
                {/* La misma pastilla que ve gerencia. Antes esta linea
                    pintaba `⭐ {teacher.rating}` con un 4,8 fijo que llegaba
                    del servidor: al alumno se le enseñaba una nota de su
                    profesor que nadie habia puesto. */}
                <div className="sub-teacher-rating">
                    <Valoracion profesor={teacher} tamano="sm" />
                </div>
            </div>

            <div className="sub-teacher-progress">
                <div className="sub-teacher-progress-row">
                    <span style={{ fontSize: 11, color: "var(--sub-tx2)" }}>Clases completadas</span>
                    <span className="sub-mono sub-adaptive-text" style={{ '--sub-c': "#00ff88", fontWeight: 800 }}>
                        {given}/{total}
                    </span>
                </div>
                <div className="sub-mini-bar">
                    <div className="sub-mini-bar-fill" style={{ width: `${pct}%`, background: "#00ff88" }} />
                </div>
            </div>

            {teacher.next_class && (
                <div className="sub-teacher-next">
                    <span style={{ fontSize: 13 }}>📅</span>
                    <div>
                        <div style={{ fontSize: 11, color: "var(--sub-tx)", fontWeight: 700 }}>
                            Próxima clase · {fmtShortDate(teacher.next_class)}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--sub-tx2)" }}>
                            {fmtTime(teacher.next_class)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  CALENDAR STRIP
// ─────────────────────────────────────────────────────────────────────────────

function CalendarStrip({ events, loading }) {
    if (loading) return (
        <div className="sub-calendar sub-card">
            <Skeleton w="40%" h={14} r={4} />
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {[0, 1, 2].map(i => <Skeleton key={i} w="100%" h={44} r={10} />)}
            </div>
        </div>
    );
    if (!events?.length) return null;

    return (
        <div className="sub-calendar sub-card">
            <div className="sub-section-title">
                <div className="sub-dot" style={{ background: "#00FFFF" }} />
                Próximos Eventos
            </div>
            <div className="sub-calendar-list">
                {events.map((ev, i) => {
                    const d = diffDays(ev.date);
                    const isToday = d === 0;
                    const accentColor = ev.type === "payment" ? "#f5a623" : "#00ff88";

                    return (
                        <div
                            key={i}
                            className="sub-cal-event"
                            style={{ borderLeft: `2px solid ${accentColor}` }}
                        >
                            <div className="sub-cal-icon">{ev.icon}</div>
                            <div className="sub-cal-info">
                                <div className="sub-cal-label">{ev.label}</div>
                                <div className="sub-cal-date">{fmtDate(ev.date)}</div>
                            </div>
                            <div
                                className="sub-cal-badge sub-adaptive-text"
                                style={{ '--sub-c': accentColor, background: `${accentColor}12`, borderColor: `${accentColor}30` }}
                            >
                                {isToday ? "Hoy" : d !== null ? `${d}d` : "—"}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ALERTS FEED
// ─────────────────────────────────────────────────────────────────────────────

const ALERT_CFG = {
    info: { color: "#00FFFF", bg: "rgba(0,255,255,.06)", border: "rgba(0,255,255,.2)" },
    success: { color: "#00ff88", bg: "rgba(0,255,136,.06)", border: "rgba(0,255,136,.2)" },
    warning: { color: "#f5a623", bg: "rgba(245,166,35,.07)", border: "rgba(245,166,35,.25)" },
    error: { color: "#ff5e7e", bg: "rgba(255,94,126,.07)", border: "rgba(255,94,126,.25)" },
};

function AlertsFeed({ alerts }) {
    const [dismissed, setDismissed] = useState([]);
    const visible = alerts?.filter(a => !dismissed.includes(a.id)) || [];

    if (!visible.length) return null;

    return (
        <div className="sub-alerts sub-card">
            <div className="sub-section-title">
                <div className="sub-dot" style={{ background: "#f5a623", animation: "sub-pulse 2s infinite" }} />
                Notificaciones
                <span className="sub-alert-count">{visible.length}</span>
            </div>
            <div className="sub-alerts-list">
                {visible.map(a => {
                    const cfg = ALERT_CFG[a.type] || ALERT_CFG.info;
                    return (
                        <div
                            key={a.id}
                            className="sub-alert-item"
                            style={{ background: cfg.bg, borderColor: cfg.border }}
                        >
                            <span className="sub-alert-icon">{a.icon}</span>
                            <div className="sub-alert-text sub-adaptive-text" style={{ '--sub-c': cfg.color }}>{a.text}</div>
                            <button
                                className="sub-alert-dismiss"
                                onClick={() => setDismissed(d => [...d, a.id])}
                            >
                                ✕
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  UPGRADE CTA — freemium only
// ─────────────────────────────────────────────────────────────────────────────

function UpgradeCTA({ trialDaysLeft }) {
    const [hovering, setHovering] = useState(false);
    const navigate = useNavigate();
    const isUrgent = trialDaysLeft <= 2;

    return (
        <div className={`sub-upgrade-cta sub-card${isUrgent ? " sub-upgrade-urgent" : ""}`}>
            <div className="sub-upgrade-glow" />
            <div className="sub-upgrade-label">
                {isUrgent ? "⚠️ Tu trial termina pronto" : "✨ Desbloquea todo tu potencial"}
            </div>
            <div className="sub-upgrade-headline">
                Tu plan actual es <strong>Freemium</strong>.<br/> Sube a
                <span className="sub-upgrade-premium"> Premium</span>
            </div>
            <div className="sub-upgrade-perks">
                {["2,000 tokens IA / mes", "12 clases con tutor", "Sin anuncios", "Progreso acelerado"].map(p => (
                    <div key={p} className="sub-upgrade-perk">
                        <span style={{ color: "#00ff88" }}>✓</span> {p}
                    </div>
                ))}
            </div>
            <button
                className="sub-upgrade-btn"
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                onClick={() => navigate("/plans")}
            >
                <span>{hovering ? "→ Ver planes →" : "⚡ Upgrade a Premium"}</span>
                <div className="sub-upgrade-btn-glow" />
            </button>
            <div className="sub-upgrade-note">
                Sin contratos · Cancela cuando quieras
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ACTIONS PANEL
// ─────────────────────────────────────────────────────────────────────────────

function ActionsPanel({ userType, planStatus, onPlanCancelled }) {
    const [confirm, setConfirm] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [cancelResult, setCancelResult] = useState(null); // "success" | "error"
    const navigate = useNavigate();

    const ACTIONS = {
        premium: [
            { key: "contact", icon: "💬", label: "Contactar soporte", color: "#00FFFF", bg: "rgba(0,255,255,.08)", border: "rgba(0,255,255,.25)" },
            { key: "history", icon: "📋", label: "Historial de pagos", color: "#00ff88", bg: "rgba(0,255,136,.08)", border: "rgba(0,255,136,.25)" },
            { key: "cancel", icon: "❌", label: "Cancelar plan", color: "#ff5e7e", bg: "rgba(255,94,126,.08)", border: "rgba(255,94,126,.25)" },
        ],
        b2b: [
            { key: "contact", icon: "💬", label: "Contactar soporte", color: "#00FFFF", bg: "rgba(0,255,255,.08)", border: "rgba(0,255,255,.25)" },
            { key: "history", icon: "📋", label: "Ver mi progreso", color: "#60a5fa", bg: "rgba(96,165,250,.08)", border: "rgba(96,165,250,.25)" },
        ],
        freemium: [
            { key: "upgrade", icon: "⚡", label: "Upgrade a Premium", color: "#c084fc", bg: "rgba(192,132,252,.12)", border: "rgba(192,132,252,.35)" },
            { key: "contact", icon: "💬", label: "Hablar con ventas", color: "#00FFFF", bg: "rgba(0,255,255,.08)", border: "rgba(0,255,255,.25)" },
        ],
    };

    const actions = ACTIONS[userType] || ACTIONS.premium;

    function handleAction(key) {
        if (key === "cancel") {
            setConfirm("cancel");
            setCancelResult(null);
            return;
        }
        if (key === "upgrade") {
            navigate("/plans");
            return;
        }
        if (key === "contact") {
            window.open("https://wa.me/5215610480746", "_blank");
            return;
        }
    }

    async function handleCancelConfirm() {
        setCancelling(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch("/api/dashboard/subscription/cancel", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setCancelResult("success");
            setConfirm(null);
            if (onPlanCancelled) onPlanCancelled();
        } catch {
            setCancelResult("error");
        } finally {
            setCancelling(false);
        }
    }

    return (
        <div className="sub-actions sub-card">
            <div className="sub-section-title">
                <div className="sub-dot" style={{ background: "#00FFFF" }} />
                Acciones rápidas
            </div>

            {/* Cancel result feedback */}
            {cancelResult === "success" && (
                <div className="sub-confirm-banner" style={{ borderColor: "rgba(0,255,136,.3)", background: "rgba(0,255,136,.06)" }}>
                    <div style={{ fontSize: 12, color: "#00ff88", fontWeight: 700 }}>✅ Cancelación registrada</div>
                    <div style={{ fontSize: 11, color: "var(--sub-tx2)", marginTop: 4 }}>
                        Tu plan seguirá activo hasta el final del período pagado.
                    </div>
                    <div className="sub-confirm-btns">
                        <button className="sub-confirm-no" onClick={() => setCancelResult(null)}>Cerrar</button>
                    </div>
                </div>
            )}
            {cancelResult === "error" && (
                <div className="sub-confirm-banner" style={{ borderColor: "rgba(255,94,126,.3)", background: "rgba(255,94,126,.08)" }}>
                    <div style={{ fontSize: 12, color: "#ff5e7e", fontWeight: 700 }}>⚠️ Error al cancelar</div>
                    <div style={{ fontSize: 11, color: "var(--sub-tx2)", marginTop: 4 }}>
                        Hubo un problema. Por favor contacta a soporte.
                    </div>
                    <div className="sub-confirm-btns">
                        <button className="sub-confirm-no" onClick={() => setCancelResult(null)}>Cerrar</button>
                        <button className="sub-confirm-yes" onClick={() => window.open("https://wa.me/5215610480746", "_blank")}>Contactar soporte</button>
                    </div>
                </div>
            )}

            {/* Cancel confirmation modal */}
            {confirm === "cancel" && !cancelResult && (
                <div className="sub-confirm-banner">
                    <div style={{ fontSize: 12, color: "#ff5e7e", fontWeight: 700 }}>❌ ¿Cancelar tu plan?</div>
                    <div style={{ fontSize: 11, color: "var(--sub-tx2)", marginTop: 4 }}>
                        Perderás acceso al final del período actual. Esta acción no se puede deshacer.
                    </div>
                    <div className="sub-confirm-btns">
                        <button className="sub-confirm-no" onClick={() => setConfirm(null)} disabled={cancelling}>
                            Volver
                        </button>
                        <button
                            className="sub-confirm-yes"
                            onClick={handleCancelConfirm}
                            disabled={cancelling}
                            style={{ opacity: cancelling ? 0.6 : 1 }}
                        >
                            {cancelling ? "Cancelando…" : "Confirmar cancelación"}
                        </button>
                    </div>
                </div>
            )}

            <div className="sub-actions-grid">
                {actions.map(a => (
                    <button
                        key={a.key}
                        className="sub-action-btn"
                        style={{ background: a.bg, color: a.color, borderColor: a.border }}
                        onClick={() => handleAction(a.key)}
                    >
                        <span className="sub-action-icon">{a.icon}</span>
                        <span className="sub-action-label">{a.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TOKENS SHOP CTA
// ─────────────────────────────────────────────────────────────────────────────

// ── COMPRAR ENERGÍA (20-09-2026) ───────────────────────────────────────────
// Dos cambios que pidió Rob:
//
//   · El aviso salía por lo GASTADO, así que aparecía «Tokens casi agotados»
//     encima de un depósito recién recargado. Ahora mira lo que le QUEDA.
//   · «Debemos de tener uno de comprar más tokens si así lo decide»: antes
//     solo había salida cuando ya iba mal. Ahora la compra está siempre a
//     mano, y el aviso rojo es solo eso, un aviso.
//
// Y el botón abre la recarga, no la página de planes: quien quiere energía
// para terminar el mes no quiere cambiar de plan.
function TokensShop({ left, max }) {
    const queda = max > 0 ? (left / max) : 1;
    const vacio = left <= 0;
    const bajo = !vacio && queda <= 0.2;
    const abre = () => window.dispatchEvent(new Event("nsl:recargar"));

    if (vacio || bajo) {
        return (
            <div className="sub-tokens-shop sub-card" data-prueba="aviso-energia">
                <div className="sub-tokens-shop-glow" />
                <div className="sub-tokens-shop-left">
                    <div className="sub-tokens-shop-icon">⚡</div>
                    <div>
                        <div className="sub-tokens-shop-title">
                            {vacio ? "Te quedaste sin Energía Spacy" : "Te queda poca Energía Spacy"}
                        </div>
                        <div className="sub-tokens-shop-sub">
                            {vacio
                                ? "Recarga para seguir practicando con la IA este mes."
                                : `Te quedan ${Number(left).toLocaleString("es-MX")}. Puedes recargar cuando quieras.`}
                        </div>
                    </div>
                </div>
                <button className="sub-tokens-shop-btn" onClick={abre} data-prueba="recargar-suscripcion">
                    + Recargar
                </button>
            </div>
        );
    }

    // Con energía de sobra no se le mete prisa, pero la puerta queda abierta.
    return (
        <button className="sub-tokens-comprar" onClick={abre} data-prueba="comprar-energia">
            ⚡ Comprar más Energía Spacy
        </button>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function SubscriptionTab() {
    const { data: apiData, loading, refetch } = useSubscriptionData();
    const cupon = useCupon();
    const navegar = useNavigate();

    // ── PRODUCCIÓN: Datos reales desde API ─────────────────────────────────
    const data = apiData || {};
    const userType = data?.user_type || "premium";

    const plan = data?.plan;
    const teacher = data?.teacher;
    const b2b = data?.b2b;
    const trial = data?.trial;
    const alerts = data?.alerts || [];
    const events = data?.upcoming_events || [];

    const tokenPct = plan
        ? Math.min(100, Math.round(((plan.tokens_used || 0) / (plan.token_limit || 1)) * 100))
        : 0;

    return (
        <div className="sub-root">
            <style>{`
`}</style>

            {/* ── Page Title ── */}
            <div className="sub-page-header">
                <div>
                    <h2 className="sub-page-title">
                        {userType === "b2b" ? "💼 Mi Plan Corporativo" :
                            userType === "freemium" ? "🌱 Mi Trial Gratuito" :
                                "💎 Mi Suscripción"}
                    </h2>
                    <p className="sub-page-sub">
                        {userType === "b2b" ? "Beneficios patrocinados por tu empresa" :
                            userType === "freemium" ? "Descubre todo lo que NSL tiene para ti" :
                                "Gestiona tu plan, tokens y clases en un solo lugar"}
                    </p>
                </div>
                <button className="sub-refresh-btn" onClick={refetch} disabled={loading}>
                    {loading ? "⏳" : "🔄"}
                </button>
            </div>

            {/* ── FREEMIUM: Trial Countdown + Upgrade (top priority) ── */}
            {userType === "freemium" && trial && (
                <>
                    <TrialCountdown trial={trial} />
                    <UpgradeCTA trialDaysLeft={trial.days_remaining} />
                </>
            )}

            {/* ── Su descuento, antes que nada: tiene fecha de caducidad ── */}
            <CuponCard cupon={cupon} onIr={() => navegar("/plans")} />

            {/* ── Alerts ── */}
            {alerts.length > 0 && <AlertsFeed alerts={alerts} />}

            {/* ── Two-column grid on desktop ── */}
            <div className="sub-grid">

                {/* LEFT COLUMN */}
                <div className="sub-col">
                    {/* B2B sponsor card OR Plan hero card */}
                    {userType === "b2b" && b2b ? (
                        <B2BSponsorCard b2b={b2b} />
                    ) : (
                        <PlanHeroCard plan={plan} userType={userType} loading={loading} />
                    )}

                    {/* Teacher card */}
                    <TeacherCard teacher={teacher} loading={loading} />

                    {/* Actions */}
                    {!loading && <ActionsPanel userType={userType} planStatus={plan?.status} onPlanCancelled={refetch} />}
                </div>

                {/* RIGHT COLUMN */}
                <div className="sub-col">
                    {/* Token Gauge */}
                    <div className="sub-card sub-tokens-card">
                        <div className="sub-section-title">
                            <div className="sub-dot" style={{ background: PLAN_COLORS[plan?.name] || "#00FFFF" }} />
                            Tokens IA
                            <span className="sub-tokens-period">este mes</span>
                        </div>
                        <TokenGauge
                            used={plan?.tokens_used || 0}
                            max={plan?.token_limit || 150}
                            left={plan?.tokens_remaining}
                            color={PLAN_COLORS[plan?.name] || "#00FFFF"}
                            loading={loading}
                        />
                        {!loading && plan && (
                            <TokensShop
                                left={plan?.tokens_remaining ?? ((plan?.token_limit || 0) - (plan?.tokens_used || 0))}
                                max={plan?.token_limit || 0}
                            />
                        )}
                    </div>

                    {/* Calendar */}
                    {events.length > 0 && <CalendarStrip events={events} loading={loading} />}

                    {/* For B2B: also show plan card */}
                    {userType === "b2b" && plan && (
                        <PlanHeroCard plan={plan} userType={userType} loading={loading} />
                    )}
                </div>
            </div>
        </div>
    );
}